/**
 * @copyright WaterStreet. All rights reserved.
 */

import {
	HttpClient
} from '@angular/common/http';
import {
	Inject,
	Injectable
} from '@angular/core';
import {
	IWorkflowRequisiteTypesDto
} from '@api/interfaces/workflow/workflow-requisite-types.dto.interface';
import {
	BaseEntityApiService
} from '@api/services/base/base-entity.api.service';
import {
	AppConstants
} from '@shared/constants/app.constants';
import {
	CacheService
} from '@shared/services/cache.service';

/**
 * A class representing the logic and services of the workflow
 * requisite types controller.
 *
 * @export
 * @class WorkflowRequisiteTypesApiService
 * @extends {BaseEntityApiService<IWorkflowRequisiteTypesDto>}
 */
@Injectable()
export class WorkflowRequisiteTypesApiService
	extends BaseEntityApiService<IWorkflowRequisiteTypesDto>
{
	/**
	 * Creates an instance of a WorkflowRequisiteTypesApiService.
	 *
	 * @param {HttpClient} HttpClient
	 * The injected http client to use in the base api service.
	 * @param {CacheService} cache
	 * The injected cache service to use in the base api service.
	 * @memberof WorkflowRequisiteTypesApiService
	 */
	public constructor(
		@Inject(HttpClient) http: HttpClient,
		@Inject(CacheService) cache: CacheService)
	{
		super();
		this.httpClient = http;
		this.cacheService = cache;
		this.endpoint =
			AppConstants.apiControllers.workflowRequisiteTypes;
	}

	/**
	 * Creates an IWorkflowRequisiteTypesDto with the specified information.
	 * This method is not implemented in the API.
	 *
	 * @param {IWorkflowRequisiteTypesDto} _entity
	 * The workflow requisite type to create.
	 * @returns {Promise<number>}
	 * The newly created item identifier.
	 * @memberof WorkflowRequisiteTypesApiService
	 */
	public async create(
		_entity: IWorkflowRequisiteTypesDto): Promise<number>
	{
		throw new Error(
			this.getHandledByOthersMessage(
				AppConstants.apiMethods.create,
				AppConstants.systemEngines.workflowEngine
			));
	}

	/**
	 * Updates an IWorkflowRequisiteTypesDto with the specified information.
	 * This method is not implemented in the API.
	 *
	 * @param {number} _id
	 * Id of the workflow requisite type to update.
	 * @param {IWorkflowRequisiteTypesDto} _entity
	 * The workflow requisite type to update.
	 * @returns {Promise<object>}
	 * Updated item identifier
	 * @memberof WorkflowRequisiteTypesApiService
	 */
	public async update(
		_id: number,
		_entity: IWorkflowRequisiteTypesDto): Promise<object>
	{
		throw new Error(
			this.getHandledByOthersMessage(
				AppConstants.apiMethods.update,
				AppConstants.systemEngines.workflowEngine
			));
	}

	/**
	 * Deletes an IWorkflowRequisiteTypesDto with the specified identifier.
	 * This method is not implemented in the API.
	 *
	 * @param {number} id
	 * The identifier of the workflow requisite type to delete.
	 * @returns {Promise<object>}
	 * An observable of the delete no-content response.
	 * @memberof WorkflowRequisiteTypesApiService
	 */
	public async delete(
		_id: number): Promise<object>
	{
		throw new Error(
			this.getHandledByOthersMessage(
				AppConstants.apiMethods.delete,
				AppConstants.systemEngines.workflowEngine
			));
	}
}